import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  settingsService,
  type BusinessInfo,
  type BusinessInfoPayload,
  type PaymentConfig,
  type SettingsFull,
  type LocaleSettings,
  type LocaleSettingsUpdateInput,
} from '@/services/settings.service';
import { settingsKeys } from '@/services/queryKeys';

export function useTenantSettings() {
  return useQuery<SettingsFull>({
    queryKey: settingsKeys.full(),
    queryFn: () => settingsService.getFull(),
    retry: 0,
  });
}

export function useLocaleSettings() {
  return useQuery<LocaleSettings>({
    queryKey: settingsKeys.locale(),
    queryFn: () => settingsService.getLocale(),
    retry: 0,
  });
}

export function useUpdateLocaleSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LocaleSettingsUpdateInput) => settingsService.updateLocale(body),
    onSuccess: (updated) => {
      qc.setQueryData<LocaleSettings>(settingsKeys.locale(), updated);
      qc.invalidateQueries({ queryKey: settingsKeys.full() });
    },
  });
}

export function useBusinessInfo() {
  return useQuery<BusinessInfo>({
    queryKey: settingsKeys.business(),
    queryFn: () => settingsService.getBusiness(),
    retry: 0,
  });
}

export function useUpdateBusinessInfo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BusinessInfoPayload) =>
      settingsService.updateBusiness(body),
    onSuccess: (updated) => {
      qc.setQueryData<BusinessInfo>(settingsKeys.business(), updated);
      qc.setQueryData<SettingsFull>(settingsKeys.full(), (current) =>
        current
          ? {
              ...current,
              business: { ...current.business, ...updated },
            }
          : current,
      );
      qc.invalidateQueries({ queryKey: settingsKeys.full() });
      qc.invalidateQueries({ queryKey: settingsKeys.business() });
    },
  });
}

export function usePaymentConfig() {
  return useQuery<PaymentConfig>({
    queryKey: settingsKeys.payment(),
    queryFn: () => settingsService.getPayment(),
    retry: 0,
  });
}

export function useUpdatePaymentConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<PaymentConfig>) =>
      settingsService.updatePayment(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: settingsKeys.full() });
      qc.invalidateQueries({ queryKey: settingsKeys.payment() });
    },
  });
}
